import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders, updateProfile, updatePassword } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import TopPromoBar from '../components/TopPromoBar';
import Header from '../components/Header';
import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import logo from '../logo.png';
import config from '../config';
import { isProfileComplete, getCurrentProfileStep, isStepComplete } from '../utils/profileCompletion';

function Profile({ user, onUserUpdate, onLogout, cart = [] }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const cartButtonRef = useRef(null);
  const { app } = config;
  const APP_NAME = app.name;
  const [orders, setOrders] = useState([]);
  
  // Account form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [shippingForm, setShippingForm] = useState({
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postal_code: '',
    shipping_country: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);
  const progressBarRef = useRef(null);
  const [animatingStep, setAnimatingStep] = useState(null);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [progressBannerTop, setProgressBannerTop] = useState('285px');

  useEffect(() => {
    // Debug: Log user data to check profile_picture
    console.log('Profile component - User data:', user);
    console.log('Profile component - Profile picture URL:', user?.profile_picture);
    
    // Initialize profile form with user data
    setProfileForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || '',
    });
    
    // Initialize shipping form
    setShippingForm({
      shipping_address: user.shipping_address || '',
      shipping_city: user.shipping_city || '',
      shipping_state: user.shipping_state || '',
      shipping_postal_code: user.shipping_postal_code || '',
      shipping_country: user.shipping_country || '',
    });
    
    // Hide completion message if profile becomes incomplete
    if (!isProfileComplete(user)) {
      setShowCompletionMessage(false);
    }
  }, [user]);

  // Fetch orders only to calculate stats for the sidebar
  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const data = await getOrders(token, 'all');
      setOrders(data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  // Calculate progress banner position based on screen size
  useEffect(() => {
    const updateBannerPosition = () => {
      const isMobile = window.innerWidth < 640;
      setProgressBannerTop(isMobile ? '285px' : '340px');
    };
    
    updateBannerPosition();
    window.addEventListener('resize', updateBannerPosition);
    return () => window.removeEventListener('resize', updateBannerPosition);
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const updatedUser = await updateProfile(profileForm, token);
      
      // Check if profile was incomplete before this update
      const wasIncomplete = !isProfileComplete(user);
      
      if (onUserUpdate) {
        onUserUpdate(updatedUser.user);
      }
      // Update local storage
      localStorage.setItem('user', JSON.stringify(updatedUser.user));
      
      // Only show success notification if profile was already complete
      if (!wasIncomplete) {
        showSuccess('Profile updated successfully!');
      }
      
      // If profile is still incomplete, animate progress to next step
      if (!isProfileComplete(updatedUser.user)) {
        setAnimatingStep(2); // Animate to step 2 (Shipping Info)
        setTimeout(() => {
          setAnimatingStep(null);
        }, 1500);
      } else {
        // Profile is complete, show completion message
        setShowCompletionMessage(true);
        setAnimatingStep(2); // Animate step 2 completion
        setTimeout(() => {
          setShowCompletionMessage(false);
          setAnimatingStep(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      showError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  // Check if profile information form (step 1) is complete
  // This checks the current form values, not just saved user data
  const isProfileFormComplete = () => {
    return !!(
      profileForm.name &&
      profileForm.email &&
      profileForm.phone
    );
  };

  const handleShippingChange = (field, value) => {
    setShippingForm({ ...shippingForm, [field]: value });
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if profile form is not complete
    if (!isProfileFormComplete()) {
      showError('Please complete all Profile Information fields before saving Shipping Information.');
      return;
    }
    
    setShippingLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const updatedUser = await updateProfile(shippingForm, token);
      
      // Check if profile was incomplete before this update
      const wasIncomplete = !isProfileComplete(user);
      
      if (onUserUpdate) {
        onUserUpdate(updatedUser.user);
      }
      localStorage.setItem('user', JSON.stringify(updatedUser.user));
      
      // Only show success notification if profile was already complete
      if (!wasIncomplete) {
        showSuccess('Shipping information updated successfully!');
      }
      
      // If profile is still incomplete, check if it's now complete
      if (!isProfileComplete(updatedUser.user)) {
        // Should not happen if shipping is the last step, but keep for safety
        setAnimatingStep(2);
        setTimeout(() => {
          setAnimatingStep(null);
        }, 1500);
      } else {
        // Profile is complete, show completion message
        setShowCompletionMessage(true);
        setAnimatingStep(2); // Animate step 2 completion
        setTimeout(() => {
          setShowCompletionMessage(false);
          setAnimatingStep(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to update shipping information:', error);
      showError(error.message || 'Failed to update shipping information. Please try again.');
    } finally {
      setShippingLoading(false);
    }
  };


  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    console.log('📁 File selected:', { 
      file, 
      isFile: file instanceof File,
      name: file?.name, 
      type: file?.type, 
      size: file?.size 
    });
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please select an image file.');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showError('Image size must be less than 10MB.');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload immediately
      handleProfilePictureUpload(file);
    } else {
      console.error('❌ No file selected!');
    }
  };

  const handleProfilePictureUpload = async (file) => {
    setProfilePictureLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      console.log('📤 Uploading profile picture:', { name: file.name, type: file.type, size: file.size });
      const response = await updateProfile({ profile_picture: file }, token);
      console.log('📥 Profile picture upload response:', response);
      console.log('📥 Response user object:', response.user);
      console.log('📥 Profile picture URL from response:', response.user?.profile_picture);
      
      if (!response.user || !response.user.profile_picture) {
        console.error('❌ Profile picture URL missing from response!', response);
        showError('Profile picture uploaded but URL was not returned. Please try again.');
        return;
      }
      
      showSuccess('Profile picture updated successfully!');
      if (onUserUpdate) {
        console.log('🔄 Updating user state with:', response.user);
        onUserUpdate(response.user);
      }
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Clear preview after a short delay to allow user state to update first
      // This ensures the R2 image URL from the backend is displayed
      setTimeout(() => {
        setProfilePicturePreview(null);
      }, 200);
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      showError(error.message || 'Failed to upload profile picture. Please try again.');
      setProfilePicturePreview(null);
    } finally {
      setProfilePictureLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.password !== passwordForm.password_confirmation) {
      showError('Passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      await updatePassword({
        current_password: passwordForm.current_password,
        password: passwordForm.password,
        password_confirmation: passwordForm.password_confirmation,
      }, token);
      showSuccess('Password updated successfully!');
      setPasswordForm({
        current_password: '',
        password: '',
        password_confirmation: '',
      });
      setShowPasswordModal(false); // Close modal on success
    } catch (error) {
      console.error('Failed to update password:', error);
      showError(error.message || 'Failed to update password. Please check your current password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `GH¢${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  // Calculate stats for sidebar
  const totalOrders = orders.length;
  // Coupon amount - sum of approved refund amounts
  const couponAmount = orders
    .filter(o => o.status === 'refund_approved' || o.refund_status === 'refund_approved')
    .reduce((sum, order) => sum + parseFloat(order.total || 0), 0);

  return (
    <div className="App">
      <TopPromoBar appName={APP_NAME} />
      <Header 
        logo={logo} 
        appName={APP_NAME}
        cartCount={0}
        onCartClick={() => navigate('/')}
        cartButtonRef={cartButtonRef}
        searchQuery=""
        onSearchChange={() => {}}
        activeFilter={null}
        onFilterChange={() => {}}
        onCategoryFilterChange={() => {}}
        user={user}
        onLoginSuccess={(user) => {
          if (onUserUpdate) {
            onUserUpdate(user);
          }
        }}
        onLogout={onLogout}
      />
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-[85px] sm:pt-[120px] pb-6 sm:pb-12">
      {/* Profile Completion Banner - Shows missing fields */}
      {!isProfileComplete(user) && <ProfileCompletionBanner user={user} />}
      
      {/* Incomplete Profile Warning Banner - Fixed below completion banner */}
      {!isProfileComplete(user) && !showCompletionMessage && (() => {
        const currentStep = getCurrentProfileStep(user);
        const stepLabels = ['Basic Info', 'Shipping Info'];
        
        return (
          <div 
            ref={progressBarRef} 
            className="fixed left-0 right-0 z-30 px-3 sm:px-6 lg:px-8"
            style={{ top: progressBannerTop }}
          >
            <div className="max-w-7xl mx-auto bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 border-2 border-yellow-300 rounded-xl p-3 sm:p-4 shadow-lg">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1 w-full">
                  <h3 className="text-sm sm:text-base font-bold text-yellow-900 mb-2 sm:mb-2.5">Complete Your Profile</h3>
                  <p className="text-xs sm:text-sm text-yellow-800 mb-3 sm:mb-3.5">
                    Please complete all required profile information before accessing other features of the application.
                  </p>
                  
                  {/* Step-by-Step Progress Indicator */}
                  <div className="relative flex items-center justify-center gap-1.5 sm:gap-2">
                    {[1, 2].map((step, index) => {
                      const isCompleted = isStepComplete(user, step);
                      const isCurrent = !isCompleted && currentStep === step;
                      const showCarOnLine = animatingStep === step + 1 && index < 1;
                      
                      return (
                        <React.Fragment key={step}>
                          {/* Step Circle */}
                          <div className="relative flex flex-col items-center z-10">
                            {isCompleted ? (
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-blue-600 bg-white flex items-center justify-center transition-all duration-500 ${animatingStep === step ? 'animate-progress-complete' : ''}`}>
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : isCurrent ? (
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-600 flex items-center justify-center transition-all duration-500 ${animatingStep === step ? 'animate-progress-pulse' : ''}`}>
                                <span className="text-white font-bold text-xs sm:text-sm">{step}</span>
                              </div>
                            ) : (
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-blue-600 bg-white flex items-center justify-center transition-all duration-500 ${animatingStep === step ? 'animate-progress-pulse' : ''}`}>
                                <span className="text-blue-600 font-bold text-xs sm:text-sm">{step}</span>
                              </div>
                            )}
                            <span className={`mt-1 text-[10px] sm:text-xs font-medium transition-colors duration-300 ${isCurrent || animatingStep === step ? 'text-blue-600' : isCompleted ? 'text-gray-700' : 'text-gray-500'}`}>
                              {stepLabels[step - 1]}
                            </span>
                          </div>
                          
                          {/* Connecting Line */}
                          {index < 1 && (
                            <div className={`flex-1 h-0.5 relative overflow-visible transition-all duration-700 ${isStepComplete(user, step + 1) || isCurrent || (animatingStep === step + 1) ? 'bg-blue-600' : 'bg-gray-300'}`}>
                              {/* Car Animation - moves along this line when animating to next step */}
                              {showCarOnLine && (
                                <div className="absolute top-1/2 animate-car-move-bounce" style={{ zIndex: 50 }}>
                                  <svg 
                                    className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" 
                                    fill="currentColor" 
                                    viewBox="0 0 24 24"
                                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                                  >
                                    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                                  </svg>
                                </div>
                              )}
                              {animatingStep === step + 1 && !isStepComplete(user, step + 1) && (
                                <div className="absolute inset-0 bg-blue-600 animate-progress-line"></div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Profile Completion Success Message - Fixed at top */}
      {showCompletionMessage && (
        <div ref={progressBarRef} className="fixed top-[85px] sm:top-[120px] left-0 right-0 z-30 px-3 sm:px-6 lg:px-8 animate-fade-in">
          <div className="max-w-7xl mx-auto bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 border-2 border-green-400 rounded-xl p-4 sm:p-5 shadow-lg">
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-500 flex items-center justify-center animate-progress-complete">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 text-center">
                <h3 className="text-base sm:text-lg font-bold text-green-900 mb-1">Profile Completed!</h3>
                <p className="text-sm sm:text-base text-green-800">
                  All required information has been filled. Your profile is now complete.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Add top margin when banners are visible to prevent content overlap */}
        {!isProfileComplete(user) && (
          <div className="h-64 sm:h-80"></div>
        )}
        
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-1 h-8 sm:w-1.5 sm:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-1 sm:mb-2">Account Settings</h1>
                <p className="text-sm sm:text-base text-gray-600 font-medium">
                  {!isProfileComplete(user) ? 'Please complete your profile to continue' : 'Manage your profile and account information'}
                </p>
              </div>
            </div>
            {isProfileComplete(user) && (() => {
              const cartItemsCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
              const hasCartItems = cartItemsCount > 0;
              
              return (
                <button
                  onClick={() => navigate(hasCartItems ? '/checkout' : '/')}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 transform"
                >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {hasCartItems ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  )}
                </svg>
                <span className="hidden sm:inline">
                  {hasCartItems ? 'Proceed to Checkout' : 'Continue Shopping'}
                </span>
                <span className="sm:hidden">
                  {hasCartItems ? 'Checkout' : 'Go Shopping'}
                </span>
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              );
            })()}
          </div>
        </div>

        {/* Add top margin when completion message is visible to prevent content overlap */}
        {showCompletionMessage && (
          <div className="h-20 sm:h-24"></div>
        )}

        {/* Main Card */}
        <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white rounded-2xl border-2 border-gray-300/50 shadow-xl shadow-gray-300/50 overflow-hidden">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-dark to-secondary opacity-60"></div>
          
          {/* Account Settings Content */}
          <div className="p-4 sm:p-6">
              <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                {/* Right Column - Profile Sidebar - Show First on Mobile */}
                <div className="lg:w-80 flex-shrink-0 order-1 lg:order-2">
                  <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-lg sm:rounded-xl border-2 border-gray-200/50 p-3 sm:p-6 shadow-lg lg:sticky lg:top-24">
                    {/* Profile Picture */}
                    <div className="flex flex-col items-center mb-3 sm:mb-6">
                      <div className="relative mb-2 sm:mb-4">
                        <div className="relative w-20 h-20 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-primary via-primary-dark to-secondary flex items-center justify-center text-white text-xl sm:text-4xl font-bold shadow-xl border-3 sm:border-4 border-white overflow-hidden">
                          {(profilePicturePreview || user?.profile_picture) && (
                            <img 
                              src={profilePicturePreview || user.profile_picture} 
                              alt={user?.name || 'Profile'} 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.error('Profile picture failed to load:', profilePicturePreview || user.profile_picture);
                                // If image fails to load, hide the img and show the default avatar
                                e.target.style.display = 'none';
                                const parent = e.target.parentElement;
                                const fallback = parent.querySelector('.profile-picture-fallback');
                                if (fallback) {
                                  fallback.style.display = 'flex';
                                }
                              }}
                              onLoad={(e) => {
                                console.log('Profile picture loaded successfully:', profilePicturePreview || user.profile_picture);
                                // Hide fallback when image loads successfully
                                const parent = e.target.parentElement;
                                const fallback = parent.querySelector('.profile-picture-fallback');
                                if (fallback) {
                                  fallback.style.display = 'none';
                                }
                              }}
                            />
                          )}
                          <span 
                            className="profile-picture-fallback absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary via-primary-dark to-secondary"
                            style={{ display: (profilePicturePreview || user?.profile_picture) ? 'none' : 'flex' }}
                          >
                            {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </span>
                          {profilePictureLoading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <svg className="animate-spin h-6 w-6 sm:h-8 sm:w-8 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          className="hidden"
                          id="profile-picture-input"
                        />
                        <label
                          htmlFor="profile-picture-input"
                          className="absolute bottom-0 right-0 w-7 h-7 sm:w-10 sm:h-10 bg-primary rounded-full flex items-center justify-center text-white shadow-lg hover:bg-primary-dark transition-colors border-2 border-white cursor-pointer"
                          title="Change profile picture"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </label>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-0.5 sm:mb-1">{user?.name || 'User'}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm break-all">{user?.email || ''}</p>
                    </div>

                    {/* Change Password Button */}
                    {isProfileComplete(user) && (
                      <div className="mb-3 sm:mb-4">
                        <button
                          onClick={() => setShowPasswordModal(true)}
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm hover:shadow-lg hover:shadow-purple-300/30 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span>Change Password</span>
                        </button>
                      </div>
                    )}

                    {/* User Details */}
                    <div className="space-y-2.5 sm:space-y-4 border-t border-gray-200 pt-3 sm:pt-6">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-7 h-7 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">ROLE</p>
                          <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{user?.role?.name || 'Customer'}</p>
                        </div>
                      </div>
                      {user?.phone && (
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">PHONE</p>
                            <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{user.phone}</p>
                          </div>
                        </div>
                      )}
                      {user?.created_at && (
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">MEMBER SINCE</p>
                            <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{formatDate(user.created_at)}</p>
                          </div>
                        </div>
                      )}
                      {user?.address && (
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">ADDRESS</p>
                            <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{user.address}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Account Summary */}
                    <div className="mt-3 sm:mt-6 pt-3 sm:pt-6 border-t border-gray-200">
                      <h4 className="text-sm sm:text-lg font-bold text-gray-900 mb-2 sm:mb-4">ACCOUNT SUMMARY</h4>
                      <div className="grid grid-cols-2 gap-2 sm:gap-4">
                        <div className="bg-blue-50 rounded-lg sm:rounded-xl p-2.5 sm:p-4 border border-blue-200">
                          <p className="text-[9px] sm:text-xs font-semibold text-gray-600 mb-1 sm:mb-2">Total Orders</p>
                          <p className="text-lg sm:text-2xl font-black text-blue-700">{totalOrders}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg sm:rounded-xl p-2.5 sm:p-4 border border-green-200">
                          <p className="text-[9px] sm:text-xs font-semibold text-gray-600 mb-1 sm:mb-2">Coupon Amount</p>
                          <p className="text-lg sm:text-2xl font-black text-green-700">{formatCurrency(couponAmount)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Left Column - Forms */}
                <div className="flex-1 space-y-4 sm:space-y-8 order-2 lg:order-1">
                {/* Profile Section */}
                <div className="bg-gradient-to-br from-blue-50/30 via-white to-white rounded-lg sm:rounded-xl border-2 border-blue-200/50 p-4 sm:p-6 shadow-lg">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Profile Information</h2>
                  </div>
                  <form onSubmit={handleProfileSubmit} className="space-y-4 sm:space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setProfileForm({ ...profileForm, phone: digitsOnly });
                        }}
                        maxLength={10}
                        inputMode="numeric"
                        autoComplete="tel"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        placeholder="(e.g. 0249000451)"
                      />
                      <p className="mt-1 text-xs text-gray-500">Numbers only, max 10 digits</p>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Address <span className="text-gray-500">(Optional)</span>
                      </label>
                      <textarea
                        value={profileForm.address}
                        onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none bg-white text-sm sm:text-base"
                        rows={3}
                        placeholder="ABC Road, XYZ City, ZYX State, 123456"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={profileLoading}
                      className="w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {profileLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : 'Save Changes'}
                    </button>
                  </form>
                </div>


                {/* Shipping Information Section - hidden; users no longer required to provide this */}
                <div className={`hidden bg-gradient-to-br from-green-50/30 via-white to-white rounded-lg sm:rounded-xl border-2 ${isProfileFormComplete() ? 'border-green-200/50' : 'border-gray-300/50'} p-4 sm:p-6 shadow-lg ${!isProfileFormComplete() ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className={`w-12 h-12 bg-gradient-to-br ${isProfileFormComplete() ? 'from-green-500 to-green-600' : 'from-gray-400 to-gray-500'} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Shipping Information</h2>
                        {!isProfileFormComplete() && (
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            Complete Profile Information above to enable this section
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {!isProfileFormComplete() && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-sm text-yellow-800">
                          Please complete all Profile Information fields (Name, Email, Phone, Address) before filling in Shipping Information.
                        </p>
                      </div>
                    </div>
                  )}
                  <form onSubmit={handleShippingSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Shipping Address <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={shippingForm.shipping_address}
                        onChange={(e) => handleShippingChange('shipping_address', e.target.value)}
                        disabled={!isProfileFormComplete()}
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none text-sm sm:text-base ${!isProfileFormComplete() ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                        rows={3}
                        placeholder="Street address, apartment, suite, etc."
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_city}
                          onChange={(e) => handleShippingChange('shipping_city', e.target.value)}
                          disabled={!isProfileFormComplete()}
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm sm:text-base ${!isProfileFormComplete() ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          State/Town <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_state}
                          onChange={(e) => handleShippingChange('shipping_state', e.target.value)}
                          disabled={!isProfileFormComplete()}
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm sm:text-base ${!isProfileFormComplete() ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                          placeholder="State/Town"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Digital Address <span className="text-gray-500">(Optional)</span>
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_postal_code}
                          onChange={(e) => handleShippingChange('shipping_postal_code', e.target.value)}
                          disabled={!isProfileFormComplete()}
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm sm:text-base ${!isProfileFormComplete() ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                          placeholder="GT-365-6598"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Country <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_country}
                          onChange={(e) => handleShippingChange('shipping_country', e.target.value)}
                          disabled={!isProfileFormComplete()}
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm sm:text-base ${!isProfileFormComplete() ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                          placeholder="Country"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={shippingLoading || !isProfileFormComplete()}
                      className={`w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r ${isProfileFormComplete() ? 'from-green-500 via-green-600 to-green-700 hover:shadow-lg hover:shadow-green-300/30 hover:-translate-y-0.5' : 'from-gray-400 to-gray-500 cursor-not-allowed'} text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0`}
                    >
                      {shippingLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : isProfileFormComplete() ? 'Save Shipping Information' : 'Complete Profile Information First'}
                    </button>
                  </form>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Bottom Navigation Button - Same as top */}
        {isProfileComplete(user) && (() => {
          const cartItemsCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
          const hasCartItems = cartItemsCount > 0;
          
          return (
            <div className="mt-6 sm:mt-8 flex justify-center">
              <button
                onClick={() => navigate(hasCartItems ? '/checkout' : '/')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 transform"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {hasCartItems ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  )}
                </svg>
                <span className="hidden sm:inline">
                  {hasCartItems ? 'Proceed to Checkout' : 'Continue Shopping'}
                </span>
                <span className="sm:hidden">
                  {hasCartItems ? 'Checkout' : 'Go Shopping'}
                </span>
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          );
        })()}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-modal-pop">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-white">Change Password</h2>
              </div>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({
                    current_password: '',
                    password: '',
                    password_confirmation: '',
                  });
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm"
                    required
                    minLength={8}
                  />
                  <p className="mt-1 text-xs text-gray-500">Password must be at least 8 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.password_confirmation}
                    onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm"
                    required
                    minLength={8}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordForm({
                        current_password: '',
                        password: '',
                        password_confirmation: '',
                      });
                    }}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {passwordLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </span>
                    ) : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
}

export default Profile;
